import React, { useMemo, useState } from 'react'
import {
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useIntl } from 'react-intl'
import { i18nMessages } from '../../Logic/CodeAccessible.i18n.Messages'
import { CapeeshColors } from '../../assets/ColorPalette'
import {
    CourseStatsSortPreset,
    formatHours,
    formatNormalizedAsPercent,
    formatStars,
} from '../../Views/Private/CourseStatisticsConstants'

const useStyles = makeStyles(() => ({
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        marginTop: '28px',
    },
    sortControl: {
        minWidth: '200px',
    },
    headerCell: {
        fontFamily: 'Rubik',
        fontWeight: 500,
        fontSize: '13px',
        color: CapeeshColors.Gray1,
    },
    row: {
        cursor: 'pointer',
    },
    selectedRow: {
        backgroundColor: CapeeshColors.Gray100,
    },
    topicName: {
        fontFamily: 'Rubik',
        fontWeight: 500,
        fontSize: '14px',
    },
    courseName: {
        fontFamily: 'Rubik',
        fontSize: '12px',
        color: CapeeshColors.Gray1,
    },
    adoptionCell: {
        minWidth: '120px',
    },
    adoptionLabel: {
        fontFamily: 'Rubik',
        fontSize: '12px',
        color: CapeeshColors.Gray1,
    },
    bar: {
        height: '6px',
        borderRadius: '3px',
        marginTop: '4px',
    },
    empty: {
        marginTop: '30px',
        color: CapeeshColors.Gray1,
    },
}))

// Column keys are API field names, which keep the backend spelling. On screen a backend Topic is a
// "topic" and a backend CourseGroup is a "course", so topicName is labelled Topic.
const columns = [
    { key: 'topicName', messageKey: 'courseStatisticsColumnTopic', numeric: false },
    { key: 'assignedUsers', messageKey: 'courseStatisticsAssignedUsers', numeric: true },
    { key: 'fractionStarted', messageKey: 'courseStatisticsPercentStarted', numeric: true },
    { key: 'fractionFinished', messageKey: 'courseStatisticsPercentFinished', numeric: true },
    { key: 'lessonsPassed', messageKey: 'courseStatisticsLessonsPassed', numeric: true },
    { key: 'hoursSpent', messageKey: 'courseStatisticsHoursSpent', numeric: true },
    { key: 'averageScoreNormalized', messageKey: 'courseStatisticsAverageScore', numeric: true },
    { key: 'averageStars', messageKey: 'courseStatisticsAverageStars', numeric: true },
]

/**
 * The ranked topic table, with the course each topic belongs to shown underneath its name.
 *
 * Aggregate only: there is deliberately no "users below X" column, and no cohort counts of any
 * kind. Those questions belong to the filter panel, where an admin has to state the threshold they
 * mean and gets a defensible list back.
 */
const CourseStatsTable = (props) => {
    const classes = useStyles()
    const intl = useIntl()

    const [sortKey, setSortKey] = useState('lessonsPassed')
    const [sortAscending, setSortAscending] = useState(false)

    const applySortPreset = (preset) => {
        if (preset === CourseStatsSortPreset.LowestAdoption) {
            setSortKey('fractionStarted')
            setSortAscending(true)
        } else if (preset === CourseStatsSortPreset.LowestAverageScore) {
            setSortKey('averageScoreNormalized')
            setSortAscending(true)
        } else {
            setSortKey('lessonsPassed')
            setSortAscending(false)
        }
    }

    const activeSortPreset = useMemo(() => {
        if (sortKey === 'fractionStarted' && sortAscending) {
            return CourseStatsSortPreset.LowestAdoption
        }
        if (sortKey === 'averageScoreNormalized' && sortAscending) {
            return CourseStatsSortPreset.LowestAverageScore
        }
        if (sortKey === 'lessonsPassed' && !sortAscending) {
            return CourseStatsSortPreset.MostUsed
        }
        return ''
    }, [sortKey, sortAscending])

    const sortedTopics = useMemo(() => {
        const topics = [...(props.courses || [])]

        topics.sort((a, b) => {
            const left = a[sortKey]
            const right = b[sortKey]

            if (typeof left === 'string' || typeof right === 'string') {
                const comparison = (left || '').localeCompare(right || '')
                return sortAscending ? comparison : -comparison
            }

            // Topics with no score at all sort last whichever way the column is pointing, so a
            // "lowest average score" sort surfaces genuinely low scores rather than empty topics.
            const leftMissing = left === null || left === undefined
            const rightMissing = right === null || right === undefined
            if (leftMissing && rightMissing) {
                return 0
            }
            if (leftMissing) {
                return 1
            }
            if (rightMissing) {
                return -1
            }

            return sortAscending ? left - right : right - left
        })

        return topics
    }, [props.courses, sortKey, sortAscending])

    const handleSort = (key) => {
        if (key === sortKey) {
            setSortAscending(!sortAscending)
            return
        }
        setSortKey(key)
        setSortAscending(key === 'topicName')
    }

    if (!props.courses || props.courses.length === 0) {
        return (
            <Typography className={classes.empty}>
                {intl.formatMessage(i18nMessages.courseStatisticsNoData)}
            </Typography>
        )
    }

    const renderAdoptionCell = (fraction, count, assigned) => {
        if (assigned === 0) {
            return <Typography className={classes.adoptionLabel}>-</Typography>
        }

        const percent = fraction === null || fraction === undefined ? 0 : Math.round(fraction * 100)

        return (
            <div className={classes.adoptionCell}>
                <Typography className={classes.adoptionLabel}>
                    {percent}% · {count}/{assigned}
                </Typography>
                <LinearProgress className={classes.bar} variant="determinate" value={Math.min(percent, 100)} />
            </div>
        )
    }

    return (
        <div>
            <div className={classes.header}>
                <Typography variant="h6">{intl.formatMessage(i18nMessages.courseStatisticsTitle)}</Typography>
                <FormControl className={classes.sortControl}>
                    <InputLabel>{intl.formatMessage(i18nMessages.courseStatisticsSortBy)}</InputLabel>
                    <Select value={activeSortPreset} onChange={(event) => applySortPreset(event.target.value)}>
                        <MenuItem value={CourseStatsSortPreset.MostUsed}>
                            {intl.formatMessage(i18nMessages.courseStatisticsSortMostUsed)}
                        </MenuItem>
                        <MenuItem value={CourseStatsSortPreset.LowestAdoption}>
                            {intl.formatMessage(i18nMessages.courseStatisticsSortLowestAdoption)}
                        </MenuItem>
                        <MenuItem value={CourseStatsSortPreset.LowestAverageScore}>
                            {intl.formatMessage(i18nMessages.courseStatisticsSortLowestScore)}
                        </MenuItem>
                    </Select>
                </FormControl>
            </div>

            <TableContainer style={{ marginTop: '12px' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.key}
                                    align={column.numeric ? 'right' : 'left'}
                                    sortDirection={sortKey === column.key ? (sortAscending ? 'asc' : 'desc') : false}>
                                    <TableSortLabel
                                        active={sortKey === column.key}
                                        direction={sortAscending ? 'asc' : 'desc'}
                                        onClick={() => handleSort(column.key)}>
                                        <Typography className={classes.headerCell}>
                                            {intl.formatMessage(i18nMessages[column.messageKey])}
                                        </Typography>
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedTopics.map((topic) => {
                            const rowKey = `${topic.courseGroupId}_${topic.topicId}`
                            const isSelected = props.selectedTopicKey === rowKey

                            return (
                                <TableRow
                                    key={rowKey}
                                    className={`${classes.row} ${isSelected ? classes.selectedRow : ''}`}
                                    onClick={() => props.onSelectTopic(isSelected ? null : topic)}>
                                    <TableCell>
                                        <Typography className={classes.topicName}>{topic.topicName}</Typography>
                                        <Typography className={classes.courseName}>
                                            {topic.courseGroupName}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">{topic.assignedUsers}</TableCell>
                                    <TableCell align="right">
                                        {renderAdoptionCell(
                                            topic.fractionStarted,
                                            topic.usersStarted,
                                            topic.assignedUsers
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {renderAdoptionCell(
                                            topic.fractionFinished,
                                            topic.usersFinished,
                                            topic.assignedUsers
                                        )}
                                    </TableCell>
                                    <TableCell align="right">{topic.lessonsPassed}</TableCell>
                                    <TableCell align="right">{formatHours(topic.hoursSpent)}</TableCell>
                                    <TableCell align="right">
                                        {formatNormalizedAsPercent(topic.averageScoreNormalized)}
                                    </TableCell>
                                    <TableCell align="right">{formatStars(topic.averageStars)}</TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    )
}

export default CourseStatsTable
